import axios from 'axios';
import { setItem } from 'helpers/session_storage_helper';

import { companyFromApi, metaFromApi, personFromApi } from 'helpers/contacts/api_helpers';
import debounceActionCreators from 'helpers/redux/debounce_action_creators';
import latestLoad from 'helpers/redux/latest_load';
import RansackQuery from 'helpers/ransack_query';
import { postify } from 'helpers/query_string_helpers';
import { map } from 'lodash';

const debouncedRetrievePeople = debounceActionCreators({ retrievePeople }, 1000);
const debouncedRetrieveCompanies = debounceActionCreators({ retrieveCompanies }, 1000);

const keys = {
  companyGroupIds: 'q[company_group_ids]',
  companyPage: 'company_page',
  companySort: 'sort[company]',
  excludeTags: 'exclude_tags',
  peopleGroupIds: 'q[people_group_ids]',
  personPage: 'person_page',
  personSort: 'sort[person]',
  relatedSearchId: 'q[related_search_id]',
  roles: 'q[contact_roles_relationship_or_contact_types_relationship_eq_any]',
  searchText: 'q[search_text]',
  tags: 'with_all_tags',
};

export const CHANGE_RECORDS = 'CHANGE_RECORDS';
export function changeRecords(companies, people, meta) {
  return {
    companies,
    people,
    meta,
    type: CHANGE_RECORDS
  };
}

export const RETRIEVE_RECORDS_STARTED = 'RETRIEVE_RECORDS_STARTED';
export function retrieveRecordsStarted() {
  return { type: RETRIEVE_RECORDS_STARTED };
}

export const SET_PERSON_TAGS = 'SET_PERSON_TAGS';
export function setPersonTags(id, tags) {
  return { id: id, tags: tags, type: SET_PERSON_TAGS };
}

export const SET_COMPANY_TAGS = 'SET_COMPANY_TAGS';
export function setCompanyTags(id, tags) {
  return { id: id, tags: tags, type: SET_COMPANY_TAGS };
}

// Each search replaces both lists, so a reply from a replaced search, or for the tab the user has left, is dropped: it
// can't put its people or companies on the other tab. Saving a group reloads its tab even after a switch away.
const startLoad = latestLoad(state => state.ui.currentTab);

export function retrievePeople() {
  return (dispatch, getState) => {
    // A Companies search still waiting out its debounce would replace these people when it fires.
    debouncedRetrieveCompanies.retrieveCompanies.cancel();
    const load = startLoad('people', getState);
    if (load.showing()) dispatch(retrieveRecordsStarted());
    const { paths, query } = getState();
    const filterOptions = { ...query };
    filterOptions.personSort = formatContactSort(filterOptions.personSort || '');
    const ransackQuery = new RansackQuery(filterOptions, keys).toRansack();
    return axios.get(paths.people.people, { params: ransackQuery }).then((response) => {
      if (!load.current()) return undefined;

      const people = map(response.data.people, record => personFromApi(record));
      const meta = metaFromApi(response.data.meta);
      setItem('contactsPeopleIds', meta.people.allIds);
      return dispatch(changeRecords([], people, meta));
    });
  };
}

export function retrieveCompanies() {
  return (dispatch, getState) => {
    debouncedRetrievePeople.retrievePeople.cancel();
    const load = startLoad('companies', getState);
    if (load.showing()) dispatch(retrieveRecordsStarted());
    const { paths, query } = getState();
    const filterOptions = { ...query };
    filterOptions.companySort = formatContactSort(filterOptions.companySort || '');
    const ransackQuery = new RansackQuery(filterOptions, keys).toRansack();
    return axios.get(paths.companies.companies, { params: ransackQuery }).then((response) => {
      if (!load.current()) return undefined;

      const companies = map(response.data.companies, record => companyFromApi(record));
      const meta = metaFromApi(response.data.meta);
      setItem('contactsCompaniesIds', meta.companies.allIds);
      return dispatch(changeRecords(companies, [], meta));
    });
  };
}

export function downloadPeopleAsCsv() {
  return (_, getState) => {
    const { paths, query, selectList } = getState();
    const params = buildCsvParams(query, 'people', selectList.selectedPersonIds);
    axios.post(paths.people.peopleToCsvParams, params)
      .then((response) => {
        window.location.assign(`${paths.people.peopleToCsv}?paramsKey=${response.data.key}`);
      });
  };
}

export function downloadCompaniesAsCsv() {
  return (_, getState) => {
    const { paths, query, selectList } = getState();
    const params = buildCsvParams(query, 'companies', selectList.selectedCompanyIds);
    axios.post(paths.companies.companiesToCsvParams, params)
      .then((response) => {
        window.location.assign(`${paths.companies.companiesToCsv}?paramsKey=${response.data.key}`);
      });
  };
}


export function delayRetrieveRecords() {
  return (dispatch, getState) => {
    const { ui } = getState();
    if (ui.currentTab === 'people') {
      debouncedRetrievePeople.retrievePeople(dispatch);
    } else {
      debouncedRetrieveCompanies.retrieveCompanies(dispatch);
    }
  };
}

function buildCsvParams(query, contactType, selectedIds) {
  const filterOptions = { ...query };
  const ransackQuery = new RansackQuery(filterOptions, keys).toRansack();
  const flatParams = { ...ransackQuery, contact_type: contactType, selected: selectedIds };
  return postify(flatParams);
}

const formatContactSort = (sorting) => {
  const [field, direction] = sorting.split(' ');

  switch (field) {
  case 'companyName': {
    return `contacts_company_name ${direction}`;
  }
  case 'lastContacted': {
    return `last_contacted ${direction}`;
  }
  case 'lastUpdatedBy': {
    return `last_updated_by ${direction}`;
  }
  case 'lastUpdatedAt': {
    return `last_updated_at ${direction}`;
  }
  case 'createdBy': {
    return `created_by ${direction}`;
  }
  case 'createdAt': {
    return `created_at ${direction}`;
  }
  case 'email': {
    return `primary_email ${direction}`;
  }
  default:
    return `${field} ${direction}`;
  }
};
